import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { AlertCircle, AlertTriangle } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import styles from './LoginPage.module.css'

const MOCK_AUTH = import.meta.env.VITE_MOCK_AUTH === 'true'

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const ssoState = params.get('sso')

  const { isAuthenticated, loginWithSSO } = useAuth()
  const [redirecting, setRedirecting] = useState(false)
  const [showManual, setShowManual] = useState(false)
  const [email, setEmail] = useState('')

  // Already authenticated → go to app
  useEffect(() => {
    if (isAuthenticated) {
      const dest = params.get('from') ?? '/dashboard'
      navigate(dest, { replace: true })
    }
  }, [isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-redirect to SSO unless there's an error state or we're in mock mode
  useEffect(() => {
    if (MOCK_AUTH || ssoState === 'error' || ssoState === 'pending') {
      setShowManual(true)
      return
    }

    setRedirecting(true)
    const redirect = setTimeout(() => loginWithSSO(), 500)
    const manual   = setTimeout(() => setShowManual(true), 3000)
    return () => { clearTimeout(redirect); clearTimeout(manual) }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault()
    loginWithSSO(email.trim() || undefined)
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.accentBar} />

        <div className={styles.brand}>
          <img
            src="https://stshrnextgenstorage.blob.core.windows.net/aptean-theme/Aptean-logo-login.png"
            alt="Aptean"
            className={styles.apteanLogo}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
        </div>

        <h1 className={styles.title}>Sign in to your account</h1>
        <p className={styles.subtitle}>Aptean Self-Service Portal</p>

        {ssoState === 'pending' && (
          <div className={`${styles.alertBanner} ${styles.alertWarning}`}>
            <AlertTriangle size={15} />
            <span>Access pending approval. Contact your administrator.</span>
          </div>
        )}
        {ssoState === 'error' && (
          <div className={`${styles.alertBanner} ${styles.alertError}`}>
            <AlertCircle size={15} />
            <span>SSO sign-in failed. Please try again.</span>
          </div>
        )}

        {/* Spinner shown during auto-redirect */}
        {redirecting && !showManual && !ssoState && (
          <div className={styles.redirecting}>
            <div className={styles.spinner} />
            <span>Redirecting to Aptean SSO...</span>
          </div>
        )}

        {/* Manual login — shown after 3s, on error/pending, or in mock mode */}
        {showManual && (
          <form onSubmit={handleNext} className={styles.form}>
            {(ssoState || MOCK_AUTH) && (
              <div className={styles.field}>
                <label className={styles.label} htmlFor="login-email">
                  Work email
                </label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={styles.input}
                  placeholder="you@aptean.com"
                />
              </div>
            )}

            <button type="submit" className={styles.submitBtn}>
              Sign in with Aptean SSO
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
